import { Component, ChangeDetectionStrategy, EventEmitter, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { TuiStringHandler, TuiValidationError } from '@taiga-ui/cdk';
import { TuiFileLike } from '@taiga-ui/kit';
import { TranslateService } from '@ngx-translate/core';
import { maxFilesLength, TuiDayTimeTransformer } from '@src/utils';
import { APP_CONFIG } from '@src/core';
import { OrganisationService } from '@src/core/services';
import { Nullable, Optional } from '@src/types/utils';
import { OrganisationView } from '@src/api';

import { LoyaltyProgramFilterService } from '../../../services';
import { selectItemMatcher } from '../../../utils';
import { SelectCity, SelectItem } from '../../../types';
import { LoyaltyProgramProductImage, LoyaltyProgramProductInput } from '../../../models';
import { MAX_FILE_COUNT } from '../../constants';
import { AbstractStepComponent } from '../abstract-step/abstract-step.component';
import { TuiFileLikeWithId } from '../../types';

import { CommonStepFormData, CommonStepFormDataControls } from './types';

@Component({
  selector: 'lp-product-common-step',
  templateUrl: './common-step.component.html',
  styleUrls: ['./common-step.component.scss'],
  // TODO: поменял стратегию на `default` т.к. основной диалоге при вызове `cdr.markForCheck()` не работает
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CommonStepComponent extends AbstractStepComponent {
  private readonly translateService = inject(TranslateService);
  private readonly loyaltyProgramFilterService = inject(LoyaltyProgramFilterService);
  private readonly organisationService = inject(OrganisationService);

  @Output() draftFormValueChanged = new EventEmitter<Optional<CommonStepFormData>>();

  currentParentOrganisation$: BehaviorSubject<OrganisationView | undefined> =
    this.organisationService.currentParentOrganisation$;

  readonly categories$ = this.loyaltyProgramFilterService.filteredCategoriesForUnion$;

  readonly categoryMatcher = selectItemMatcher;
  readonly categoryStringify: TuiStringHandler<SelectItem> = item => item.name;

  coverFileName = '';
  acceptFileMask = APP_CONFIG.fileTypes.image.join(',');
  maxFileSize = APP_CONFIG.fileSizeMax.image;

  form = new FormGroup<CommonStepFormDataControls>({
    categories: new FormControl<Optional<Array<SelectItem>>>(undefined, [Validators.required]),
    cities: new FormControl<Optional<Array<SelectCity>>>(undefined, [Validators.required]),
    name: new FormControl<Optional<string>>(undefined, [Validators.required, Validators.maxLength(1000)]),
    description: new FormControl<Optional<string>>(undefined, [Validators.required, Validators.maxLength(2000)]),
    descriptionShort: new FormControl<Optional<string>>(undefined, [Validators.maxLength(50)]),
    discountText: new FormControl<Optional<string>>(undefined, [Validators.maxLength(200)]),
    sortOrder: new FormControl<Optional<number>>(undefined),
    files: new FormControl<Optional<Array<TuiFileLikeWithId | File>>>(undefined, [
      Validators.required,
      maxFilesLength(MAX_FILE_COUNT),
    ]),
    rejectedFiles: new FormControl<Array<TuiFileLike>>([], { nonNullable: true, validators: [maxFilesLength(0)] }),
    dateExpiration: new FormControl<Optional<string>>(undefined),
  });

  get filesControl() {
    return this.form.controls.files as FormControl<Array<TuiFileLike> | undefined>;
  }

  get rejectedFilesControl() {
    return this.form.controls.rejectedFiles as FormControl<Array<TuiFileLike>>;
  }

  filesError = new TuiValidationError(
    this.translateService.instant('components.loyaltyCommonStep.alerts.errors.filesValidation'),
  );
  rejectedFilesError = new TuiValidationError(
    this.translateService.instant('components.loyaltyCommonStep.alerts.errors.rejectedFilesValidation'),
  );

  override ngOnInit(): void {
    super.ngOnInit();

    this.loyaltyProgramFilterService.getCategoriesForUnion(false);

    this.filesControl.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(files => {
      if (!files || !files.length) {
        this.coverFileName = '';
      }

      if (!this.coverFileName && files?.[0]) {
        this.setCover(files[0]);
      } else {
        this.update();
      }
    });

    this.form.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(draft => {
      this.draftFormValueChanged.emit(draft);
    });

    this.emitForm(this.form);
  }

  emitChanges() {
    const { name, description, descriptionShort, discountText, sortOrder, categories, cities, files, dateExpiration } =
      this.form.value as CommonStepFormData;

    return {
      name: name ?? '',
      description: description ?? '',
      descriptionShort,
      discountText: discountText ?? '',
      sortOrder: sortOrder,
      categories: categories ?? [],
      cities: cities,
      dateExpiration: TuiDayTimeTransformer.tuiDayToDate(dateExpiration),
      images: files?.map<LoyaltyProgramProductImage>(file => {
        if (file instanceof File) {
          return {
            file,
            isCover: this.coverFileName === file.name,
          };
        }

        return {
          id: file.id,
          file: file instanceof File ? file : file.name,
          isCover: this.coverFileName === file.name,
        };
      }),
    };
  }

  setCover(file: TuiFileLike) {
    this.coverFileName = file.name;
    this.update();
  }

  onSearchCategories(query: Nullable<string>) {
    this.loyaltyProgramFilterService.searchCategoriesForUnion(query);
  }

  protected patchProduct(product: LoyaltyProgramProductInput) {
    const files = product.images?.reduce<Array<TuiFileLikeWithId | File>>((acc, item) => {
      if (item.file instanceof File) {
        acc.push(item.file);
      } else {
        acc.push({ id: item.id, name: item.file as string });
      }

      return acc;
    }, []);

    this.form.patchValue({
      name: product.name,
      description: product.description,
      descriptionShort: product.descriptionShort,
      discountText: product.discountText,
      sortOrder: product.sortOrder,
      categories: product.categories,
      cities: product.cities,
      files,
      dateExpiration: product.dateExpiration
        ? TuiDayTimeTransformer.dateToTuiDay(product.dateExpiration)
        : product.dateExpiration,
    });

    const { file } = product.images?.find(item => item.isCover) ?? {};
    if (file) {
      this.coverFileName = file instanceof File ? file.name : file;
    }
  }
}
